import {
  deleteBusinessMessage,
  fetchThreadMessage,
  forwardMessageSharedSpace,
  sendThreadMessage,
  updateMessage,
} from '@/core/apis/share-space-api';

import {
  MessageContent,
  OptimisticMessage,
  PaginationParams,
  SendThreadMessagePayload,
  ThreadMessage,
  ThreadMessagesResponse,
  UserInfo,
} from '../types';
import { MessageTextExtractor } from '../utils/messageTextExtractor';

export class MessageService {
  private static instance: MessageService;
  private messageCache: Map<string, ThreadMessage[]> = new Map();
  private pendingMessages: Map<string, OptimisticMessage> = new Map();

  private constructor() {}

  static getInstance(): MessageService {
    if (!MessageService.instance) {
      MessageService.instance = new MessageService();
    }
    return MessageService.instance;
  }

  // Fetch thread messages with caching
  async getThreadMessages(
    threadId: string,
    params: PaginationParams = { limit: 20, offset: 0 },
    isBusinessThread: boolean = true,
  ): Promise<ThreadMessagesResponse> {
    try {
      const cacheKey = `${threadId}_${params.offset}_${params.limit}`;

      // Use appropriate API based on thread type
      const response = await fetchThreadMessage(threadId, params);

      if (response?.data) {
        // Process messages to ensure they have display text
        const processedMessages = this.processMessages(response.data.messages || []);

        // Update cache
        this.updateMessageCache(threadId, processedMessages, params.offset || 0);

        return {
          ...response,
          data: {
            ...response.data,
            messages: processedMessages,
          },
        };
      }

      throw new Error('No data received from server');
    } catch (error) {
      console.error('Failed to fetch thread messages:', error);
      throw error;
    }
  }

  // Send a new message with optimistic update
  async sendMessage(
    threadId: string,
    text: string,
    contentIds: string[] = [],
    replyToId?: string,
  ): Promise<ThreadMessage> {
    // Build payload according to backend requirements
    const payload: SendThreadMessagePayload = {};

    // Only include non-empty fields
    if (text?.trim()) {
      payload.message = text;
    }

    if (contentIds.length > 0) {
      payload.contents = contentIds;
    }

    if (replyToId) {
      payload.replyTo = replyToId;
    }

    // Create optimistic message
    const optimisticMessage = this.createOptimisticMessage(threadId, text, contentIds, undefined, replyToId);

    // Add to pending messages
    this.pendingMessages.set(optimisticMessage._tempId, optimisticMessage);

    try {
      const response = await sendThreadMessage(threadId, payload);

      if (response?.data?.message) {
        // Remove optimistic message
        this.pendingMessages.delete(optimisticMessage._tempId);

        // Process the returned message (response.data.message contains the actual message)
        const processedMessage = this.processMessage(response.data.message);

        // Update cache with real message
        this.addMessageToCache(threadId, processedMessage);

        return processedMessage;
      }

      throw new Error('Failed to send message');
    } catch (error) {
      // Remove failed optimistic message
      this.pendingMessages.delete(optimisticMessage._tempId);
      console.error('Failed to send message:', error);
      throw error;
    }
  }

  // Delete a message
  async deleteMessage(messageId: string, threadId: string, isBusinessThread: boolean = true): Promise<void> {
    try {
      await deleteBusinessMessage(messageId);
      // Remove from cache
      this.removeMessageFromCache(threadId, messageId);
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw error;
    }
  }

  // Update/edit a message
  async updateMessage(
    messageId: string,
    threadId: string,
    updates: Partial<SendThreadMessagePayload>,
  ): Promise<ThreadMessage> {
    try {
      const response = await updateMessage(messageId, updates);

      if (response?.data) {
        const processedMessage = this.processMessage(response.data);
        this.updateMessageInCache(threadId, messageId, processedMessage);
        return processedMessage;
      }

      throw new Error('Failed to update message');
    } catch (error) {
      console.error('Failed to update message:', error);
      throw error;
    }
  }

  // Forward message to other threads
  async forwardMessage(messageId: string, targetThreadIds: string[]): Promise<void> {
    try {
      await forwardMessageSharedSpace(messageId, targetThreadIds);
    } catch (error) {
      console.error('Failed to forward message:', error);
      throw error;
    }
  }

  // Process messages to ensure they have display text
  private processMessages(messages: any[]): ThreadMessage[] {
    return messages.map((msg) => this.processMessage(msg));
  }

  private processMessage(message: any): ThreadMessage {
    // Ensure message has display text
    const displayText = MessageTextExtractor.extractDisplayText(message);

    const createdBy = message.createdBy
      ? {
          ...message.createdBy,
          ProfileImage: message.createdBy.ProfileImage || message.createdBy.DisplayPic,
        }
      : undefined;

    return {
      ...message,
      createdBy,
      _displayText: displayText, // Add computed display text
      contents: message.contents || [],
    };
  }

  // Create optimistic message for instant UI feedback
  private createOptimisticMessage(
    threadId: string,
    text: string,
    contentIds: string[],
    currentUser?: UserInfo,
    replyToId?: string,
  ): OptimisticMessage {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Use the provided user when available, falling back to a placeholder
    const user = currentUser || {
      Id: 'current_user',
      Name: 'You',
    };

    const contents: MessageContent[] = [];

    // Add text content if present
    if (text?.trim()) {
      contents.push({
        contentType: 'text',
        content: { text },
      });
    }

    return {
      _id: tempId,
      _tempId: tempId,
      _optimistic: true,
      message: text,
      contents,
      createdBy: user,
      createdAt: new Date().toISOString(),
      read: true,
      messageType: 'USER',
    };
  }

  // Cache management
  private updateMessageCache(threadId: string, messages: ThreadMessage[], offset: number): void {
    const cached = this.messageCache.get(threadId) || [];

    if (offset === 0) {
      // Replace cache for first page
      this.messageCache.set(threadId, messages);
    } else {
      // Append for pagination
      const updated = [...messages, ...cached];
      // Remove duplicates
      const unique = Array.from(new Map(updated.map((m) => [m._id, m])).values());
      this.messageCache.set(threadId, unique);
    }
  }

  private addMessageToCache(threadId: string, message: ThreadMessage): void {
    const cached = this.messageCache.get(threadId) || [];
    this.messageCache.set(threadId, [...cached, message]);
  }

  private removeMessageFromCache(threadId: string, messageId: string): void {
    const cached = this.messageCache.get(threadId) || [];
    const filtered = cached.filter((m) => m._id !== messageId);
    this.messageCache.set(threadId, filtered);
  }

  private updateMessageInCache(threadId: string, messageId: string, updatedMessage: ThreadMessage): void {
    const cached = this.messageCache.get(threadId) || [];
    const updated = cached.map((m) => (m._id === messageId ? updatedMessage : m));
    this.messageCache.set(threadId, updated);
  }

  // Get cached messages
  getCachedMessages(threadId: string): ThreadMessage[] {
    return this.messageCache.get(threadId) || [];
  }

  // Get pending optimistic messages
  getPendingMessages(threadId: string): OptimisticMessage[] {
    return Array.from(this.pendingMessages.values());
  }

  // Clear cache for a thread
  clearThreadCache(threadId: string): void {
    this.messageCache.delete(threadId);
  }

  // Clear all cache
  clearAllCache(): void {
    this.messageCache.clear();
    this.pendingMessages.clear();
  }
}
